/**
 * Plugin Stability Store — Wave 22.13.9
 *
 * Central Zustand store that tracks per-plugin error rates, crash counts,
 * slow handler detections, capability denials, manifest validation errors,
 * sandbox violations, and an overall stability score.
 *
 * Used by:
 *   - PluginErrorBoundary (UI crash detection)
 *   - Action handler wrappers
 *   - Event handler wrappers
 *   - ContentSync subscriber wrappers
 *   - Execution timeout detector
 *   - Crash recovery system
 *   - DevTools Stability Dashboard
 */

import { create } from 'zustand';

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type StabilityEventKind =
  | 'ui-error'
  | 'action-error'
  | 'event-error'
  | 'sync-error'
  | 'slow-handler'
  | 'crash'
  | 'capability-denial'
  | 'manifest-error'
  | 'sandbox-violation';

export interface PluginStabilityEvent {
  id: string;
  pluginId: string;
  kind: StabilityEventKind;
  message: string;
  detail?: Record<string, unknown>;
  timestamp: string;
}

export interface PluginStabilityRecord {
  pluginId: string;
  errors: PluginStabilityEvent[];
  warnings: PluginStabilityEvent[];
  slowHandlers: PluginStabilityEvent[];
  crashCount: number;
  lastCrashTimestamp: string | null;
  capabilityDenials: PluginStabilityEvent[];
  manifestErrors: PluginStabilityEvent[];
  sandboxViolations: PluginStabilityEvent[];
  /** 0–100 score: 100 = perfectly stable, 0 = completely broken */
  stabilityScore: number;
  /** Plugin disabled by crash recovery? */
  disabledByCrashRecovery: boolean;
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const MAX_EVENTS_PER_CATEGORY = 100;
const CRASH_THRESHOLD = 3;
const SLOW_HANDLER_THRESHOLD_MS = 250;

let _nextEventId = 1;
function nextEventId(): string {
  return `stab-${Date.now()}-${_nextEventId++}`;
}

// ────────────────────────────────────────────────────────
// Score calculation
// ────────────────────────────────────────────────────────

function calculateStabilityScore(record: PluginStabilityRecord): number {
  let score = 100;
  score -= record.errors.length * 5;
  score -= record.crashCount * 15;
  score -= record.slowHandlers.length * 2;
  score -= record.capabilityDenials.length * 3;
  score -= record.manifestErrors.length * 10;
  score -= record.sandboxViolations.length * 8;
  return Math.max(0, Math.min(100, score));
}

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────

interface PluginStabilityState {
  records: Map<string, PluginStabilityRecord>;

  /** Record a stability event for a plugin. */
  recordEvent(pluginId: string, kind: StabilityEventKind, message: string, detail?: Record<string, unknown>): PluginStabilityEvent;

  /** Record a crash (increments crash count, may trigger disable). */
  recordCrash(pluginId: string, message: string, detail?: Record<string, unknown>): boolean;

  /** Check if a plugin exceeds the crash threshold. */
  shouldDisable(pluginId: string): boolean;

  /** Mark a plugin as disabled by crash recovery. */
  disablePlugin(pluginId: string): void;

  /** Re-enable a plugin (user action). */
  reEnablePlugin(pluginId: string): void;

  /** Get the stability record for a plugin. */
  getRecord(pluginId: string): PluginStabilityRecord;

  /** Get all records as an array. */
  getAllRecords(): PluginStabilityRecord[];

  /** Get the stability score for a plugin. */
  getScore(pluginId: string): number;

  /** Reset a plugin's stability record. */
  resetPlugin(pluginId: string): void;

  /** Full reset. */
  reset(): void;
}

function createEmptyRecord(pluginId: string): PluginStabilityRecord {
  return {
    pluginId,
    errors: [],
    warnings: [],
    slowHandlers: [],
    crashCount: 0,
    lastCrashTimestamp: null,
    capabilityDenials: [],
    manifestErrors: [],
    sandboxViolations: [],
    stabilityScore: 100,
    disabledByCrashRecovery: false,
  };
}

function trimArray<T>(arr: T[], max: number): T[] {
  return arr.length > max ? arr.slice(-max) : arr;
}

export const usePluginStabilityStore = create<PluginStabilityState>((set, get) => ({
  records: new Map(),

  recordEvent(pluginId, kind, message, detail) {
    const event: PluginStabilityEvent = {
      id: nextEventId(),
      pluginId,
      kind,
      message,
      detail,
      timestamp: new Date().toISOString(),
    };

    set((state) => {
      const records = new Map(state.records);
      const record = { ...(records.get(pluginId) ?? createEmptyRecord(pluginId)) };

      switch (kind) {
        case 'ui-error':
        case 'action-error':
        case 'event-error':
        case 'sync-error':
          record.errors = trimArray([...record.errors, event], MAX_EVENTS_PER_CATEGORY);
          break;
        case 'slow-handler':
          record.slowHandlers = trimArray([...record.slowHandlers, event], MAX_EVENTS_PER_CATEGORY);
          break;
        case 'capability-denial':
          record.capabilityDenials = trimArray([...record.capabilityDenials, event], MAX_EVENTS_PER_CATEGORY);
          break;
        case 'manifest-error':
          record.manifestErrors = trimArray([...record.manifestErrors, event], MAX_EVENTS_PER_CATEGORY);
          break;
        case 'sandbox-violation':
          record.sandboxViolations = trimArray([...record.sandboxViolations, event], MAX_EVENTS_PER_CATEGORY);
          break;
        case 'crash':
          record.crashCount++;
          record.lastCrashTimestamp = event.timestamp;
          break;
      }

      record.stabilityScore = calculateStabilityScore(record);
      records.set(pluginId, record);
      return { records };
    });

    return event;
  },

  recordCrash(pluginId, message, detail) {
    get().recordEvent(pluginId, 'crash', message, detail);
    const shouldDisable = get().shouldDisable(pluginId);
    if (shouldDisable) {
      get().disablePlugin(pluginId);
    }
    return shouldDisable;
  },

  shouldDisable(pluginId) {
    const record = get().records.get(pluginId);
    if (!record) return false;
    return record.crashCount >= CRASH_THRESHOLD;
  },

  disablePlugin(pluginId) {
    set((state) => {
      const records = new Map(state.records);
      const record = { ...(records.get(pluginId) ?? createEmptyRecord(pluginId)) };
      record.disabledByCrashRecovery = true;
      record.stabilityScore = 0;
      records.set(pluginId, record);
      return { records };
    });
    console.error(`[PluginStability] Plugin "${pluginId}" disabled by crash recovery (threshold: ${CRASH_THRESHOLD})`);
  },

  reEnablePlugin(pluginId) {
    set((state) => {
      const records = new Map(state.records);
      const record = { ...(records.get(pluginId) ?? createEmptyRecord(pluginId)) };
      record.disabledByCrashRecovery = false;
      record.crashCount = 0;
      record.stabilityScore = calculateStabilityScore(record);
      records.set(pluginId, record);
      return { records };
    });
  },

  getRecord(pluginId) {
    return get().records.get(pluginId) ?? createEmptyRecord(pluginId);
  },

  getAllRecords() {
    return Array.from(get().records.values());
  },

  getScore(pluginId) {
    return get().records.get(pluginId)?.stabilityScore ?? 100;
  },

  resetPlugin(pluginId) {
    set((state) => {
      const records = new Map(state.records);
      records.delete(pluginId);
      return { records };
    });
  },

  reset() {
    set({ records: new Map() });
  },
}));

// ────────────────────────────────────────────────────────
// Exports
// ────────────────────────────────────────────────────────

export { CRASH_THRESHOLD, SLOW_HANDLER_THRESHOLD_MS };
